import React from 'react'
import { observable, action, computed } from 'mobx'
import { CommonContainer } from '../Common'
import { getPersonalOfferData } from 'src/utils/mySiteUtils'
import {
  i18nTranslate,
  getContentFromCS,
  getLocaleCodeFromUrl,
  enablePORedesignFeature,
  trackErrorInInstana,
} from 'src/utils'
import {
  getPOAnalyticsSessionId,
  setPOAnalyticsSessionId,
} from 'src/utils/poAnalyticsUtils'
import { APPConfig, getLocalStorage, convertToBoolean } from 'config/appConfig'

class PersonalOfferContainer extends CommonContainer {
  @observable personalOfferData = null
  @observable isPersonalOfferLoading = false
  @observable personalOfferError = null
  @observable poSiteShopHeaderResponse = null
  @observable poSiteSponsor = null
  @observable sponsorLookupResponse = null
  @observable poPriceType = null
  @observable productOffersCSValues = null
  @observable headerApiError = null
  @observable savedPitchID = null

  // Per-offer in-flight analytics promises. Prevents duplicate
  // `productOfferAnalytics` API calls when `trackAnalytics` is invoked
  // multiple times for the same offerId before the first response resolves
  // (e.g. rapid re-renders / re-mounts of the PersonalOffer page).
  trackAnalyticsInFlight = {}

  constructor() {
    super()
  }

  @computed
  get isValidSponsor() {
    return this.sponsorLookupResponse?.valid !== false
  }

  /**
   * Whether the new Equinox PO experience (V2) is enabled via app config.
   * Centralized here so view code doesn't have to re-derive it.
   */
  @computed
  get isEquinoxPOEnabled() {
    return convertToBoolean(APPConfig?.getAppConfig()?.enableProductOfferV2)
  }

  /**
   * Host-side sponsor invalidation signal used to show NoOfferHere fallback
   * (only meaningful when the V2 experience is enabled).
   */
  @computed
  get hostInvalidSponsor() {
    return this.isEquinoxPOEnabled && !this.isValidSponsor
  }

  /**
   * Resolvers for the NoOfferHere UI. They accept the transient
   * MFE-published request payload (held by the view) and fall back to
   * container state when absent. Kept as plain methods (not @computed)
   * because the input is owned by the view.
   */
  getNoOfferUserId = (mfeRequest = null) =>
    mfeRequest?.userId || this.poSiteSponsor || ''

  getNoOfferPitchId = (mfeRequest = null) =>
    mfeRequest?.pitchId || this.savedPitchID || ''

  getNoOfferReason = (mfeRequest = null) =>
    mfeRequest?.reason ||
    (this.hostInvalidSponsor ? 'invalid_sponsor' : 'invalid_link')

  @action
  setPOSiteShopHeaderResponse = response => {
    this.poSiteShopHeaderResponse = response
    this.updatePOPriceType()
  }

  @action
  updatePOPriceType = () => {
    if (enablePORedesignFeature()) {
      return
    }
    if (this.poSiteShopHeaderResponse) {
      const locale = getLocaleCodeFromUrl() || null
      const lang = locale?.split('_')[0] || 'en'
      const market = this.poSiteShopHeaderResponse?.market || {}
      const priceType = market?.lang?.[lang]?.priceType || ''
      this.poPriceType = priceType?.toLowerCase() || ''
    }
  }

  @action
  fetchPersonalOfferData = async (props = {}) => {
    try {
      this.isPersonalOfferLoading = true
      this.personalOfferError = null

      const response = await getPersonalOfferData(props)

      if (response?.statusCode === 200) {
        this.personalOfferData = response.data || response
      } else {
        this.personalOfferError =
          response?.message ||
          i18nTranslate('po.error.fetch', 'Failed to fetch personal offers')
      }

      return response
    } catch (error) {
      this.personalOfferError =
        error.message ||
        i18nTranslate('po.error.api', 'Error fetching personal offers')
      console.error('Personal Offer API Error:', error)
      return null
    } finally {
      this.isPersonalOfferLoading = false
    }
  }

  @action
  clearPersonalOfferData = () => {
    this.personalOfferData = null
    this.personalOfferError = null
    this.isPersonalOfferLoading = false
  }

  /**
   * Calls the product-offer analytics endpoint and stores the returned
   * analytics session id in localStorage (`po_audit_sessionId`). The host
   * uses this value to set the `x-nuskin-analyticsId` header on subsequent
   * cart / checkout / search calls (see `src/utils/apiUtils.js`).
   *
   * Mirrors the MFE `useAnalyticsTrack` hook so the call can run from the
   * host (storefront) instead of the personal-offer MFE.
   *
   * @param {string} offerId - The personal offer / pitch id.
   * @returns {Promise<string>} resolved analytics session id (or '').
   */
  @action
  trackAnalytics = async offerId => {
    if (!offerId) return ''
    // De-dupe (persisted): if this offer already has a sessionId in the
    // `po_audit_sessionId` map, reuse it and skip the analytics API call.
    // The entry is only removed on successful order submit (per-offer) or
    // on logout (full clear), so re-entering the same offer page before
    // order confirmation must not trigger a fresh call.
    const existingSessionId = getPOAnalyticsSessionId(offerId)
    if (existingSessionId) {
      return existingSessionId
    }
    // De-dupe (in-flight): if a request for this offerId is already in
    // progress, return its promise so concurrent callers share the same API
    // call instead of issuing duplicates.
    if (this.trackAnalyticsInFlight[offerId]) {
      return this.trackAnalyticsInFlight[offerId]
    }
    const requestPromise = (async () => {
      try {
        const params = {
          endPointName: 'productOfferAnalytics',
          credentials: 'omit',
          isToSendStoreId: false,
          isToSendLocaleInQueryParam: false,
          isToRemoveCommonHeaders: ['x-store-id', 'locale'],
          queryParams: { offerId },
          headers: {
            client_id: APPConfig.getActiveAppConfig()?.client_id || '',
          },
        }
        const response = await this.fetchResponse(params)
        const sessionId = response?.analyticsId || ''
        if (sessionId) {
          // Store the analytics sessionId keyed by offerId so each offer
          // keeps its own session. The host uses this map to set the
          // `x-nuskin-analyticsId` header on subsequent cart / checkout
          // calls.
          setPOAnalyticsSessionId(offerId, sessionId)
        }
        return sessionId
      } catch (error) {
        trackErrorInInstana({
          errorReport: 'Error in trackAnalytics for product offer',
          errorData: { response: error, offerId },
        })
        return ''
      } finally {
        delete this.trackAnalyticsInFlight[offerId]
      }
    })()
    this.trackAnalyticsInFlight[offerId] = requestPromise
    return requestPromise
  }

  @action
  getProductOffersCSValues = async () => {
    try {
      const data = await getContentFromCS('nextgen_productoffer')
      if (data) {
        this.productOffersCSValues = data
      }
    } catch (error) {
      console.error('Error fetching product offers CS values:', error)
    }
  }

  getSocialIcons = () => {
    const socialInfo =
      this.productOffersCSValues?.defaultMysiteInfo?.socialInfo || []
    if (!Array.isArray(socialInfo)) return []
    return socialInfo
      .map((socialAccount, index) => ({
        id: index + 1,
        url: socialAccount?.socialUrl || '',
      }))
      .filter(socialObject => socialObject?.url)
  }

  get getDefaultBannerForPO() {
    if (
      this.productOffersCSValues &&
      !this.productOffersCSValues.defaultMysiteInfo
    ) {
      return this.productOffersCSValues
    }

    const defaultInfo = this.productOffersCSValues?.defaultMysiteInfo || {}
    const bannerColors = defaultInfo?.bannerFontBgColor || {}

    return {
      imageUrl: defaultInfo?.defaultLogo?.url,
      imageAltText: this.productOffersCSValues?.labelMysiteShopImage,
      shopTitle: defaultInfo?.defaultShopTitle,
      shopDescription: defaultInfo?.defaultDescription,
      socialTitleText: defaultInfo?.labelSocialTitleText,
      email: defaultInfo?.emailAddress,
      socialIcons: this.getSocialIcons(),
      bannerProps: {
        backgroundColor: bannerColors?.backgroundColor,
        headlineFont: bannerColors?.headlineFont,
        paragraphFont: bannerColors?.paragraphFont,
      },
      expirationDate: this.productOffersCSValues?.defaultExpiresDate,
    }
  }

  hasPOSiteHeaderApiFailed = () => {
    return (
      !!this.headerApiError ||
      !this.poSiteShopHeaderResponse ||
      Object.keys(this.poSiteShopHeaderResponse).length === 0
    )
  }
}

const personalOfferContainer = new PersonalOfferContainer()
export { personalOfferContainer, PersonalOfferContainer }
export default personalOfferContainer
